<template>
  <v-form 
    ref="installmentForm"
    class="lot-form d-flex flex-column white pa-4 overflow-y-auto" 
    style="border-radius: 15px"
  >
    <block v-if="isLoading"></block>
    <!-- Header -->
    <div class="action-bar d-flex align-center justify-space-between pa-2 rounded-lg">
      <!-- Title -->
      <div 
        class="title p-0 m-0 text-h5"
        style="color: white !important;"
      >
        Generar comprobante de pago
      </div>
      <!-- Action bar -->
      <div class="d-flex">
        <Button class="mr-2" color="warning" @click="restore">
          <div class="d-flex align-center justify-center">
            <v-icon class="mr-1">{{ installmentData ? 'mdi-restore' : 'mdi-broom'}}</v-icon>
            <div>{{ installmentData ? 'Restaurar' : 'Limpiar'}}</div>
          </div>
        </Button>  
        <Button class="mr-2" color="success" @click="save" :disabled="!isFormValid">
          <div class="d-flex align-center justify-center">
            <v-icon class="mr-1">mdi-cash-plus</v-icon>
            <div>Generar</div>
          </div>
        </Button>  
        <Button 
           icon="mdi-close"
           color="grey"
           @click="$emit('close')"
         >
         </Button>
      </div>
    </div>
    <!-- Basic info -->
    <div class="basic-info">
      <div class="section-header d-flex align-center pt-3">
        <v-icon class="mr-1">mdi-note-text</v-icon>
        <h3>Datos generales</h3>
      </div>
      <v-row class="mt-3">
        <!-- Lot Name -->
        <v-col cols="12" sm="12" lg="6" xl="4">
          <StaticField 
            label="Nombre del lote"
            icon="mdi-card-text"
            :value="lotData.LotName"
          />
        </v-col>
        <v-col cols="12" sm="12" lg="6" xl="4">
          <StaticField 
            label="Mes"
            icon="mdi-calendar"
            :value="installmentData.PeriodName"
          />
        </v-col>
        <v-col cols="12" sm="6" lg="4" xl="3">
          <!-- Lot number -->
           <StaticField 
            label="Número de Lote"
            icon="mdi-pound"
            :value="lotData.LotNumber"
          />
        </v-col>
        <v-col cols="12" sm="6" lg="4" xl="3">
          <!-- Lot number -->
           <StaticField 
            label="Número de Polígono"
            icon="mdi-pound"
            :value="lotData.PolygonNumber"
          />
        </v-col>
        <v-col cols="12" sm="6" lg="4" xl="3">
          <!-- Installment number -->
           <StaticField 
            label="Número de cuota"
            icon="mdi-pound"
            :value="installmentData.InstallmentNumber"
          />
        </v-col>
        <v-col cols="12" sm="6" lg="4" xl="3">
          <!-- Total ammount -->
           <StaticField 
            label="Monto total"
            icon="mdi-cash"
            :value="parseFloat(installmentData.TotalAmount)"
            :currency="currency"
          />
        </v-col>
        <v-col cols="12" sm="6" lg="4" xl="3">
          <!-- Base amount -->
           <StaticField 
            label="Monto base"
            icon="mdi-cash"
            :value="parseFloat(installmentData.BaseAmount)"
            :currency="currency"
          />
        </v-col>
        <v-col cols="12" sm="6" lg="4" xl="3">
          <!-- Interest amount -->
          <StaticField 
            label="Monto interés"
            icon="mdi-cash-plus"
            :value="parseFloat(installmentData.InterestAmount)"
            :currency="currency"
          />
        </v-col>
        <v-col cols="12" sm="6" lg="4" xl="3">
          <!-- Base Balance -->
           <StaticField 
            label="Balance base"
            icon="mdi-cash-clock"
            :value="parseFloat(installmentData.BaseBalance)"
            :currency="currency"
          />
        </v-col>
        <v-col cols="12" sm="6" lg="4" xl="3">
          <!-- Interest Balance -->
          <StaticField 
            label="Balance interés"
            icon="mdi-cash-clock"
            :value="parseFloat(installmentData.InterestBalance)"
            :currency="currency"
          />
        </v-col>
      </v-row>
    </div>
    <div class="amounts-info">
      <div class="section-header d-flex align-center py-3">
        <v-icon class="mr-1">mdi-cash-multiple</v-icon>
        <h3>Datos de pago</h3>
      </div>
      <v-row class="mt-3">
        <v-col cols="12" sm="6" lg="4" xl="3">
          <DatePicker
            class="mb-5 mb-sm-0"
            label="Fecha de pago"
            v-model="form.PaymentDate"
            @dateSelected="(newValue) => (form.PaymentDate = newValue)"
            :value="form.PaymentDate"
            dense
          />
        </v-col>
        <v-col cols="12" sm="6" lg="4" xl="3">
          <!-- Total payment -->
          <div class="d-flex">
            <v-text-field
              v-model="form.BasePayment"
              label="Pago base"
              prepend-icon="mdi-cash"
              type="number"
              min="0"
              :max="installmentData.BaseBalance"
              :disabled="installmentData.BaseBalance <= 0"
              outlined
              dense
              class="mr-2"
              @focus="$event.target.select()"
              :rules=" [,
                (v) => (parseFloat(v) >= 0) || `Value must be at least ${0}`,
                (v) => (parseFloat(v) <= parseFloat(installmentData.BaseBalance)) 
                  || `El valor no puede ser mayor que el balance base restante.`,
              ]"
            />
            <Button 
              v-if="installmentData.BaseBalance > 0"
              color="grey" 
              size="small"
              @click="form.BasePayment = parseFloat(installmentData.BaseBalance)"
            >
              Máx
            </Button>
          </div>
        </v-col>
        <v-col cols="12" sm="6" lg="4" xl="3">
          <!-- Interest payment -->
          <div class="d-flex">
            <v-text-field
              v-model="form.InterestPayment"
              label="Pago interés"
              prepend-icon="mdi-cash-plus"
              type="number"
              min="0"
              :max="installmentData.InterestBalance"
              outlined
              dense
              class="mr-2"
              :disabled="installmentData.InterestBalance <= 0"
              @focus="$event.target.select()"
              :rules=" [,
                (v) => (v >= 0) || `Value must be at least ${0}`,
                (v) => (v <= installmentData.InterestBalance) 
                  || `El valor no puede ser mayor que el balance de interés restante.`,
              ]"
            />
            <Button 
              v-if="installmentData.InterestBalance > 0"
              color="grey" 
              size="small"
              @click="form.InterestPayment = parseFloat(installmentData.InterestBalance)"
            >
              Máx
            </Button>
          </div>
        </v-col>
      </v-row>
    </div>
    <div class="additional-amounts-info">
      <div class="section-header d-flex align-center py-3">
        <v-icon class="mr-1">mdi-cash-multiple</v-icon>
        <h3>Datos de pago adicional</h3>
      </div>
      <v-row class="mt-3">
        <v-col cols="12" sm="6" lg="4" xl="3">
          <!-- Additional base payment -->
          <v-text-field
            v-model="form.AdditionalBasePayment"
            label="Pago base adicional"
            prepend-icon="mdi-cash-fast"
            type="number"
            min="0"
            outlined
            dense
            @focus="$event.target.select()"
          />
        </v-col>
        <v-col cols="12" sm="6" lg="4" xl="3">
          <!-- Additional Interest payment -->
          <v-text-field
            v-model="form.AdditionalInterestPayment"
            label="Pago interés adicional"
            prepend-icon="mdi-cash-fast"
            type="number"
            min="0"
            outlined
            dense
            @focus="$event.target.select()"
            :disabled="installmentData.InterestBalance <= 0"
          />
        </v-col>
      </v-row>
    </div>
    <alert
      v-model="alert.show"
      :type="alert.type"
      :title="alert.title"
      :message="alert.message"
      @input="alert.onInputEvent"
      @action="alert.onActionEvent"
    />
  </v-form>
</template>

<script>
import Block from '@/components/Block.vue';
import Button from '@/components/Button.vue';
import Alert from '@/components/Alert.vue';
import moment from 'moment';
import DatePicker from "@/components/DatePicker";
import StaticField from '@/components/StaticField.vue';

export default {
  name: 'InstallmentForm',
  components: {
    Block,
    Button,
    Alert,
    DatePicker,
    StaticField,
  },
  props: {
    lotData: {
      type: Object,
    },
    planData: {
      type: Object,
    },
    installmentData: {
      type: Object,
    },
  },
  data() {
    return {
      isLoading: false,
      defaultValues: {
        BasePayment: 0.0,
        InterestPayment: 0.0,
        AdditionalPayment: 0.0,
        AdditionalBasePayment: 0.0,
        AdditionalInterestPayment: 0.0,
        PaymentDate: moment().format('DD/MM/YYYY'),
      },
      form: { ...this.defaultValues },
      currency: '$',
      services: [],
      customers: [],
      sellers: [],
      saleTypes: [],
      reservationTypes: [],
      alert: {
        type: 'success',
        show: false,
        header: '',
        body: '',
        onInputEvent: this.closeAlert,
        onActionEvent: this.closeAlert,
      },
      showAddServiceDialog: false,
      formHasBeenSubmitted: false,
      touchedFields: {},
    }
  },
  computed: {
    isFormValid() {
      let isValid = true;

      if (!this.valueInRange(this.form.BasePayment, 0, this.installmentData.BaseBalance)) {
        isValid = false;
      }

      if (!this.valueInRange(this.form.InterestPayment, 0, this.installmentData.InterestBalance)) {
        isValid = false;
      }

      if (this.form.BasePayment <= 0
        && this.form.InterestPayment <= 0
        && this.form.AdditionalBasePayment <= 0
        && this.form.AdditionalInterestPayment <= 0
      ) {
        isValid = false;
      }

      return isValid;
    }
  },
  methods: {
    restore() {
      if (!this.installmentData) {
        this.form = {
          ...this.defaultValues
        };
      } else {
        const installmentData = this.installmentData
        console.log('INSTALLMENT DATA')
        console.log(installmentData)

        this.form = {
          ...this.defaultValues
        }
      }

      this.formHasBeenSubmitted = false;
      this.touchedFields = {};
      this.$refs.installmentForm.validate();
    },
    valueInRange(value, min, max) {
      let numericValue = Number(value);

      return numericValue <= max && numericValue >= min;
    },
    showAlert(title, message, type = 'success', onInputEvent, onActionEvent) {
      this.alert.type = type;
      this.alert.title = title;
      this.alert.message = message;
      this.alert.show = true;
      this.alert.onInputEvent = onInputEvent 
        ? onInputEvent
        : this.closeAlert;
      this.alert.onActionEvent = onActionEvent 
        ? onActionEvent
        : this.closeAlert;
      
    },
    closeAlert() {
      this.alert.show = false;
    },
    onFieldInput(field) {
      // Mark field as touched when cleared or changed (especially if cleared)
      if (!this.form[field]) {
        this.$set(this.touchedFields, field, true);
      }
    },
    onFieldBlur(field) {
      // Mark field as touched to start showing errors if invalid
      this.$set(this.touchedFields, field, true);
    },
    fieldRequiredRule(fieldName, v) {
      if (!this.formHasBeenSubmitted && !this.touchedFields[fieldName]) {
        // don't show error initially
        return true;
      }

      return !!v || 'Este campo es obligatorio.';
    },
    parseDate(dateString) {
      const date = moment(dateString).utcOffset(6000)

      if (!date.isValid()) {
        console.log(`Fecha inválida: ${dateString}`)
      }

      return date.isValid
          ? moment(dateString).format('DD/MM/YYYY')
          : moment().format('DD/MM/YYYY')
    },
    async save() {
      this.isLoading = true;
      try {
        this.formHasBeenSubmitted = true;
        const valid = this.$refs.installmentForm.validate();

        if (!valid) {
          this.showAlert('Advertencia', 'Por favor rellene todos los campos requeridos.', 'warning');
          this.isLoading = false;
          return;
        }

        const ID_Business = JSON.parse(localStorage.getItem("user")).businessid || "";
        const ID_Branch = JSON.parse(localStorage.getItem("user")).branch || "";

        const requestData = {
          lotId: this.lotData.ID_RealStateLot,
          planId: this.planData.ID_FinancingPlan,
          installmentId: this.installmentData.ID_FinancingInstallment,
          ...this.form,
          AdditionalBasePayment: parseFloat(this.form.AdditionalBasePayment),
          AdditionalInterestPayment: parseFloat(this.form.AdditionalInterestPayment),
          ID_Business,
          ID_Branch,
        };

        console.log('INST DATA')
        console.log(requestData)

        let response = null;

        // Call process payment endpoint
        response = await this.$API.realstate.processInstallmentPayment({
          data: requestData,
        })
        
        this.showAlert(
          'Comprobante de pago generado', 
          'El comprobante se ha creado exitosamente.', 
          'success',
          () => {
            this.$emit('installment-updated');
            this.closeAlert();
          }
        );

        console.log(response)
      } catch(err) {
        console.log(err);
        this.showAlert('Error', 'Ha ocurrido agregando el lote.', 'error');
      }
      this.isLoading = false;
    },
  },
  mounted() {
    console.log('MOUNTED')
    console.log(this.planData)
    this.restore();
    this.currency = JSON.parse(localStorage.getItem("branch")).Currency;
  }
}
</script>

<style scoped>
.section-header {
  border-top: 1px solid #E0E0E0;
  margin: 2rem 0 1rem 0;
}

.section-header h3 {
  color: #0000008a !important;
}


.action-bar {
  background-color: #616161;
}
</style>